/**
 * Search input for filtering vehicles by line name.
 * Press Enter to add a line filter, allowing multiple lines to be shown.
 * Includes autocomplete suggestions based on currently tracked lines.
 */

import { useState, useCallback, useMemo } from 'react';
import { useFilterStore } from '../../stores/filterStore';
import { useVehicleStore } from '../../stores/vehicleStore';

export interface SearchInputProps {
  /** Additional CSS class */
  className?: string;
  /** Placeholder text */
  placeholder?: string;
}

export function SearchInput({
  className = '',
  placeholder = 'Add line filter...',
}: SearchInputProps) {
  const lineFilters = useFilterStore((state) => state.lineFilters);
  const addLineFilter = useFilterStore((state) => state.addLineFilter);
  const removeLineFilter = useFilterStore((state) => state.removeLineFilter);
  const clearLineFilters = useFilterStore((state) => state.clearLineFilters);
  const vehicles = useVehicleStore((state) => state.vehicles);

  const [inputValue, setInputValue] = useState('');

  // Extract unique line names from currently tracked vehicles
  const availableLines = useMemo(() => {
    const lineSet = new Set<string>();
    vehicles.forEach((vehicle) => {
      if (vehicle.line?.short_name) {
        lineSet.add(vehicle.line.short_name);
      }
    });
    // Sort alphanumerically (numbers first, then letters)
    return Array.from(lineSet).sort((a, b) => {
      const aNum = parseInt(a, 10);
      const bNum = parseInt(b, 10);
      const aIsNum = !isNaN(aNum);
      const bIsNum = !isNaN(bNum);
      if (aIsNum && bIsNum) return aNum - bNum;
      if (aIsNum) return -1;
      if (bIsNum) return 1;
      return a.localeCompare(b);
    });
  }, [vehicles]);

  // Handle input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  // Handle Enter key to add filter
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && inputValue.trim()) {
        e.preventDefault();
        addLineFilter(inputValue.trim());
        setInputValue('');
      }
    },
    [inputValue, addLineFilter]
  );

  // Handle remove filter chip
  const handleRemoveFilter = useCallback(
    (filter: string) => {
      removeLineFilter(filter);
    },
    [removeLineFilter]
  );

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-3 ${className}`}>
      {/* Filter chips */}
      {lineFilters.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {lineFilters.map((filter) => (
            <span
              key={filter}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 rounded-full text-xs font-medium"
            >
              {filter}
              <button
                onClick={() => handleRemoveFilter(filter)}
                className="ml-0.5 hover:text-blue-600 dark:hover:text-blue-200"
                aria-label={`Remove ${filter} filter`}
              >
                ×
              </button>
            </span>
          ))}
          <button
            onClick={clearLineFilters}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 px-1"
            aria-label="Clear all line filters"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Input field */}
      <div className="relative">
        <label htmlFor="line-filter-input" className="sr-only">
          Add line filter (press Enter to add)
        </label>
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg
            className="h-4 w-4 text-gray-400 dark:text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <input
          id="line-filter-input"
          type="text"
          list="available-lines-list"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          aria-describedby="filter-hint"
        />
        {/* Autocomplete suggestions from tracked lines */}
        <datalist id="available-lines-list">
          {availableLines
            .filter((line) => !lineFilters.includes(line.toUpperCase()))
            .map((line) => (
              <option key={line} value={line} />
            ))}
        </datalist>
      </div>
      <p id="filter-hint" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Type line name and press Enter to add filter
        {availableLines.length > 0 && ` (${availableLines.length} lines available)`}
      </p>
    </div>
  );
}
